import { ValueType } from '@convergence/types';
import { useCallback, useEffect } from 'react';
import { Button } from '@convergence/components';
import { deepClone } from '../util';
import {
  InputValidationError,
  validateInput
} from '../../../lib/validate-analysis-input';
import { useStateLocalStorage } from '../../../hooks/useStateLocalStorage';
import { AnalysisInputRoot } from './analysis-root-input';

type AnalysisInputMap = Record<
  number,
  {
    schema: ValueType;
    name: string;
    errors: { keyPath: string[]; message: string }[];
  }
>;

export type AnalysisInputProps = {
  onSubmit: (value: AnalysisInputMap) => void;
  inputs: {
    name: string;
    id: number;
    valueSchema: {
      id: number;
      name: string;
      schema: ValueType;
    };
  }[];
};

function getInputMap(inputs: AnalysisInputProps['inputs']) {
  return inputs.reduce((acc, cur) => {
    return {
      ...acc,
      [cur.id]: {
        schema: cur.valueSchema.schema,
        name: cur.name,
        errors: []
      }
    };
  }, {} as AnalysisInputMap);
}

export function AnalysisInputs(props: AnalysisInputProps) {
  const [values, setValues] = useStateLocalStorage<AnalysisInputMap>(
    'analysis-input',
    getInputMap(props.inputs)
  );

  useEffect(() => {
    setValues(getInputMap(props.inputs));
  }, [props.inputs]);

  const handleSubmit = useCallback(() => {
    let clone = deepClone(values);

    Object.entries(values).map(([idString, { schema }]) => {
      const id = +idString;
      try {
        validateInput(schema);
        clone = {
          ...clone,
          [id]: { ...clone[id]!, errors: [] }
        };
        props.onSubmit(clone);
      } catch (err) {
        if (err instanceof InputValidationError) {
          clone = {
            ...clone,
            [id]: {
              ...clone[id]!,
              errors: [{ message: err.message, keyPath: err.path }]
            }
          };
        } else {
          throw new Error(`Unrecognized error: ${err}`);
        }
      }
    });
    setValues(clone);
  }, [values]);

  return (
    <div>
      {Object.entries(values).map(([id, { schema, name, errors }]) => (
        <AnalysisInputRoot
          key={`analysis-input-${id}-${name}`}
          analysisSchemaId={+id}
          value={schema}
          name={name}
          errors={errors}
          onChange={(v) => {
            setValues((old) => {
              return {
                ...old,
                [id]: { ...old[+id], schema: v }
              };
            });
          }}
        />
      ))}
      <Button onClick={() => handleSubmit()}>Launch</Button>
    </div>
  );
}
